// SectionStepsComponent.js
import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

// Cloudflare KeyPoint Vector
const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVector.svg';

const CARD_HEIGHT_WITH_TEXT = 348;
const CARD_HEIGHT_NO_TEXT = 304;

// Footer spacing + estimated height (safe for 1–2 lines)
const SECTION_BOX_PADDING_TOP = 32;
const TITLE_TO_SUBTITLE_GAP = 16;
const SUBTITLE_TO_CARDS_GAP = 40;
const FOOTER_TOP_GAP = 40;
const FOOTER_BOTTOM_PADDING = 48;
const FOOTER_ESTIMATED_HEIGHT = 48; // ~2 lines at 1rem/1.5rem

const SectionGreyStepsComponent = ({ data, color }) => {
  if (!data) return null;

  const {
    title,
    subtitle,
    steps = [],
    footer,
    numberColourWhite = false,
  } = data;

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (headerRef.current) {
      const height = headerRef.current.getBoundingClientRect().height;
      setHeaderHeight(height);
    }
  }, [title, subtitle]);

  const cardsTop =
    SECTION_BOX_PADDING_TOP + headerHeight + SUBTITLE_TO_CARDS_GAP;

  const cardsHeight = Math.max(
    ...steps.map((step) =>
      step.text ? CARD_HEIGHT_WITH_TEXT : CARD_HEIGHT_NO_TEXT
    )
  );

  const footerExtraHeight = footer
    ? FOOTER_TOP_GAP + FOOTER_ESTIMATED_HEIGHT + FOOTER_BOTTOM_PADDING
    : 32;

  const greyBoxHeight = cardsTop + cardsHeight + footerExtraHeight;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {/* Grey Box */}
        <Box
          sx={{
            position: 'relative',
            width: '1010px',
            height: `${greyBoxHeight}px`,
            backgroundColor: color === 'blue' ? '#162F3B' : '#E3E6E8',
            borderRadius: '34px',
            px: '32px',
            py: '32px',
            overflow: 'visible',
            zIndex: 1,
          }}
        >
          {/* Title + Subtitle */}
          <Box ref={headerRef}>
            <ScrollFadeWrapper variant="text">
              <Typography
                component={'h3'}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '3rem',
                  lineHeight: '3.25rem',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  color: color === 'blue' ? '#FFFFFF' : '#162F3B',
                  whiteSpace: 'pre-line',
                }}
              >
                {title}
              </Typography>
            </ScrollFadeWrapper>

            <ScrollFadeWrapper variant="text" delay={0.2}>
              <Typography
                component={'p'}
                sx={{
                  mt: `${TITLE_TO_SUBTITLE_GAP}px`,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1.5rem',
                  lineHeight: '1.75rem',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  color: color === 'blue' ? '#FFFFFF' : '#162F3B',
                  maxWidth: 946,
                  mx: 'auto',
                  whiteSpace: 'pre-line',
                }}
              >
                {subtitle}
              </Typography>
            </ScrollFadeWrapper>
          </Box>

          {/* Cards */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: `${cardsTop}px`,
              display: 'flex',
              gap: '24px',
              zIndex: 3,
            }}
          >
            {steps.map((step, i) => (
              <ScrollFadeWrapper key={i} delay={0.4 + i * 0.15}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '389px',
                    height: step.text
                      ? CARD_HEIGHT_WITH_TEXT
                      : CARD_HEIGHT_NO_TEXT,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '32px',
                    pt: '120px',
                    pr: '64px',
                    pb: '64px',
                    pl: '64px',
                    boxShadow:
                      '0px 1px 2px rgba(162,172,177,0.20), 0px 12px 24px -4px rgba(22,47,59,0.12)',
                  }}
                >
                  {/* Number Badge with Cloudflare Vector */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-20px',
                      left: '32px',
                      width: '96px',
                      height: '96px',
                      zIndex: 5,
                    }}
                  >
                    <Image
                      width={96}
                      height={96}
                      src={KEYPOINT_VECTOR_URL}
                      alt="Key point illustration"
                      sx={{ width: '100%', height: '100%' }}
                    />

                    <Typography
                      component={'p'}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                        fontFamily: 'Pally, Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '4.5rem',
                        lineHeight: '4.25rem',
                        letterSpacing: '-0.03em',
                        color: numberColourWhite ? '#FFFFFF' : '#162F3B',
                      }}
                    >
                      {step.number}
                    </Typography>
                  </Box>

                  {/* Card Heading */}
                  <Typography
                    component={'h3'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.625rem',
                      lineHeight: '1.875rem',
                      color: '#162F3B',
                      mb: '32px',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {step.heading}
                  </Typography>

                  {/* Card Text */}
                  {step.text && (
                    <Typography
                      component={'p'}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.002em',
                        color: '#162F3B',
                      }}
                    >
                      {step.text}
                    </Typography>
                  )}
                </Box>
              </ScrollFadeWrapper>
            ))}
          </Box>

          {/* Optional Footer (under cards) */}
          {footer && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${cardsTop + cardsHeight + FOOTER_TOP_GAP}px`,
                width: '100%',
                px: '32px',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 3,
              }}
            >
              <Typography
                component="p"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.002em', // ~ -0.2%
                  textAlign: 'center',
                  textDecorationStyle: 'solid',
                  textDecorationThickness: 'auto',
                  textDecorationOffset: 'auto',
                  color: '#162F3B',
                  maxWidth: 946,
                }}
              >
                {footer}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default SectionGreyStepsComponent;
